<template>
  <div class="report-attendance-step">
    <section class="attendance-upload-panel">
      <div class="attendance-upload-panel__header">
        <div>
          <div class="attendance-upload-panel__eyebrow">Attendance Upload</div>
          <h3>上传签到与考勤数据</h3>
        </div>
        <el-tag type="primary" effect="plain">双源数据</el-tag>
      </div>
      <div class="attendance-upload-grid">
        <report-gen-sign-in
          ref="signInRef"
          :rrma-id="rrmaId"
          :merge-state="mergeRecord"
          compact-upload
          upload-only
          hide-merge-table
          @busy-change="setBusy"
          @merge-updated="onSignInUpdated"
        />
        <!-- 考试成绩多条：exam-only 仅显示考试标签条（上传考试 + 点击标签弹框查看详情），整合表由下方 mergeTableRef 统一展示 -->
        <report-gen-attendance
          :rrma-id="rrmaId"
          :merge-record="mergeRecord"
          exam-only
          @busy-change="setBusy"
          @merge-updated="onExamUpdated"
        />
      </div>
    </section>

    <section class="attendance-upload-panel attendance-record-panel">
      <div class="attendance-upload-panel__header">
        <div>
          <div class="attendance-upload-panel__eyebrow">Recognition Result</div>
          <h3>签到表识别结果</h3>
        </div>
        <el-tag type="success" effect="plain">可编辑识别数据</el-tag>
      </div>
      <report-gen-sign-in
        ref="signRecordRef"
        :rrma-id="rrmaId"
        records-only
        hide-merge-table
        @busy-change="setBusy"
        @merge-updated="refreshMergeTable"
      />
    </section>

    <section class="attendance-upload-panel attendance-merge-panel">
      <div class="attendance-upload-panel__header">
        <div>
          <div class="attendance-upload-panel__eyebrow">Merged Data</div>
          <h3>签到+考试整合数据</h3>
        </div>
        <el-tag type="warning" effect="plain">最终整合表</el-tag>
      </div>
      <report-gen-attendance ref="mergeTableRef" :rrma-id="rrmaId" :merge-record="mergeRecord" table-only />
    </section>
  </div>
</template>

<script>
import ReportGenSignIn from './reportGenSignIn.vue'
import ReportGenAttendance from './reportGenAttendance.vue'
import { getRtReportModelUserSignMergeList } from '@/api/api'

export default {
  name: 'ReportAttendanceStep',
  components: { ReportGenSignIn, ReportGenAttendance },
  props: {
    rrmaId: { type: [Number, String], default: null },
    initialRecord: { type: Object, default: null }
  },
  data () {
    return {
      mergeRecord: null,
      pendingCount: 0,
      initialized: false
    }
  },
  watch: {
    rrmaId: {
      immediate: true,
      handler (val) {
        if (val) this.refreshMergeState()
      }
    },
    initialRecord: {
      immediate: true,
      handler (val) {
        if (val && !this.mergeRecord) {
          this.mergeRecord = val
          this.$emit('progress-change', val)
        }
      }
    }
  },
  methods: {
    setBusy (busy) {
      this.pendingCount += busy ? 1 : -1
      if (this.pendingCount < 0) this.pendingCount = 0
      this.$emit('busy-change', this.pendingCount > 0)
    },
    async refreshMergeState () {
      if (!this.rrmaId) return
      this.setBusy(true)
      try {
        const res = await getRtReportModelUserSignMergeList({ rrmaId: this.rrmaId })
        let record = null
        if (Array.isArray(res.data)) {
          record = res.data.length ? res.data[res.data.length - 1] : null
        } else if (res.data && res.data.rrmusmId) {
          record = res.data
        }
        this.mergeRecord = record
        this.initialized = true
        this.$emit('progress-change', record)
      } finally {
        this.setBusy(false)
      }
    },
    // 签到变更（上传/编辑/删除签到识别）：需刷新整合表 + 签到OCR列表
    async onSignInUpdated () {
      this.setBusy(true)
      try {
        await this.refreshMergeState()
        // signInRef 是 upload-only 上传实例，不渲染识别列表，且签到组件上传后已自行 fetchList，
        // 此处不再重复刷新；仅刷新签到识别结果列表（signRecordRef）+ 整合表
        if (this.$refs.signRecordRef && this.$refs.signRecordRef.fetchList) {
          await this.$refs.signRecordRef.fetchList()
        }
        if (this.$refs.mergeTableRef && this.$refs.mergeTableRef.applyMergeRecord) {
          this.$refs.mergeTableRef.applyMergeRecord(this.mergeRecord)
        }
      } finally {
        this.setBusy(false)
      }
    },
    // 考试变更（新增/编辑/清空考试）：考试组件已自行轻量刷新整合表，此处仅同步主表记录 + 更新下方整合表
    // 关键优化：考试上传不会改变签到识别数据，因此不再刷新签到OCR列表，避免冗余调用
    async onExamUpdated () {
      this.setBusy(true)
      try {
        await this.refreshMergeState()
        if (this.$refs.mergeTableRef && this.$refs.mergeTableRef.applyMergeRecord) {
          this.$refs.mergeTableRef.applyMergeRecord(this.mergeRecord)
        }
      } finally {
        this.setBusy(false)
      }
    },
    // 兼容旧调用入口（保留方法名，等同签到变更刷新）
    async refreshMergeTable () {
      await this.onSignInUpdated()
    }
  }
}
</script>

<style lang="scss" scoped>
.report-attendance-step {
  width: 100%;
}

.attendance-upload-panel {
  margin-bottom: 18px;
  padding: 18px;
  border: 1px solid rgba(203, 213, 225, 0.82);
  border-radius: 18px;
  background:
    radial-gradient(circle at 8% 0, rgba(37, 99, 235, 0.08), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.92));
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.06);
}

.attendance-upload-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  h3 {
    margin: 4px 0 0;
    color: #0f172a;
    font-size: 18px;
    font-weight: 850;
  }
}

.attendance-upload-panel__eyebrow {
  color: #2563eb;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.attendance-upload-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  align-items: stretch;
}

@media screen and (max-width: 1180px) {
  .attendance-upload-grid {
    grid-template-columns: 1fr;
  }
}
</style>
